import React from 'react';
import {GridColDef, GridRenderCellParams, GridValueGetter} from '@mui/x-data-grid-pro';
import {Delete, OpenInNewOutlined} from '@mui/icons-material';
import {Button} from '@mui/material';
import {useLazyQuery, useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {OVRecommends_ovrecommends} from '../../../schema-types/OVRecommends';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {themeColors} from '../../../theme/Theme';
import {
  RECOMMENDATION_TYPES,
  serverToRecommendationTypeLabel,
} from '../../../constants/SolutionType';
import {URGENCY} from '../../../constants/Urgency';
import {OpenInCuro} from '../../../utils/getCuroURL';
import {RenderColor} from '../../../components/EditorGrid/GridRenderComponents';

import {useAccountContext} from '../../../contexts/AccountContext';
import {
  deleteSelectedRecommendationFactory,
  onCompletedDeleteRecommendationFactory,
  updateDeleteRecommendation,
} from './RecommendsGridHelpers';
import {
  DELETE_OV_RECOMMENDATION,
  OV_RECOMMENDATION_ASSOCIATED_RECCS,
} from './ovPotQueries';
import {
  OvrecommendAssociatedReccs,
  OvrecommendAssociatedReccsVariables,
} from '../../../schema-types/OvrecommendAssociatedReccs';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {
  DeleteOvrecommend,
  DeleteOvrecommendVariables,
} from '../../../schema-types/DeleteOvrecommend';
import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';
import {useReviewOVRecommends} from '../../../hooks/useOVRecommends';
import {renderCellDateTime} from '../../../utils/renderCellDateTime';
import {useGetSppReview} from '../../../pages/AccountInstructionsPage/AccountInstructionsPage';
import {BusinessUnitEnum, GlobalPermissionEnum} from '../../../schema-types/globalTypes';
import {useFeaturePermission} from '../../../contexts/FeatureBoundary';

export const getReccStatus = (recc: OVRecommendation) => {
  if (recc.recommends?.some((x) => x.isFailedSubmission)) {
    return 'Error';
  }
  return recc.recommends && recc.recommends.length > 0 && recc.recommends[0]?.curoId
    ? 'Submitted'
    : 'Draft';
};
export const valueGetterStatus: GridValueGetter<OVRecommendation> = (params, row) => {
  return getReccStatus(row);
};

export const valueGetterFinalised: GridValueGetter<OVRecommendation> = (params, row) => {
  return row.isFinalised ? 'Finalised' : 'Not finalised';
};

export const valueGetterAdvice: GridValueGetter<OVRecommendation> = (params, row) => {
  const label = serverToRecommendationTypeLabel(row.solutionType);
  return label;
};

export const valueGetterName: GridValueGetter<OVRecommendation> = (params, row) => {
  return row.name || 'No name given';
};

export const valueGetterGoal: GridValueGetter<OVRecommendation> = (params, row) => {
  return row.goal?.name;
};

export const valueGetterUrgency: GridValueGetter<OVRecommendation> = (params, row) => {
  return row.urgency;
};

function rowActionsContent(row: OVRecommendation, showDelete: boolean) {
  return (
    <>
      {showDelete ? <RecommendActions recommendId={row.id as string} /> : null}
      <Button
        className="open-recc"
        variant="text"
        onClick={() => {
          window.open(`./recommends-builder?ovrecommendId=${row.id}`, '_blank');
        }}
      >
        <OpenInNewOutlined />
      </Button>
      <ShowIfDebugWrapper>
        {row.recommends?.length
          ? row.recommends.map((r, index) => {
              return (
                <OpenInCuro key={r.curoId} curoId={r.curoId} curoType="recommendation">
                  {index}
                </OpenInCuro>
              );
            })
          : null}
      </ShowIfDebugWrapper>
    </>
  );
}

export function recommendGridRowActions(params: GridRenderCellParams<OVRecommendation>) {
  const {row} = params;
  return rowActionsContent(row, getReccStatus(row) !== 'Submitted');
}

const SppRowActions: React.FC<{row: OVRecommendation}> = ({row}) => {
  const isAdmin = useFeaturePermission([GlobalPermissionEnum.can_access_admin_features]);
  return rowActionsContent(row, isAdmin);
};

export function sppRecommendGridRowActions(
  params: GridRenderCellParams<OVRecommendation>
) {
  return <SppRowActions row={params.row} />;
}

type RecommendColumnType = GridColDef<OVRecommends_ovrecommends> & {
  field: keyof OVRecommends_ovrecommends;
};

export const ehonsRecommendsColumns: RecommendColumnType[] = [
  {
    field: 'id',
    headerName: 'Status',
    flex: 0.7,
    headerAlign: 'center',

    renderCell: RenderColor({
      Submitted: themeColors.lightGreen,
      Draft: themeColors.greyLight,
      Failed: themeColors.red,
    }),
    valueGetter: valueGetterStatus,
  },
  {
    field: 'isFinalised',
    headerName: 'Finalised',
    flex: 0.7,
    headerAlign: 'center',

    renderCell: RenderColor({
      Finalised: themeColors.lightGreen,
      'Not finalised': themeColors.greyLight,
    }),
    valueGetter: valueGetterFinalised,
  },
  {
    field: 'solutionType',
    headerName: 'Advice',
    flex: 1,
    headerAlign: 'center',

    renderCell: RenderColor({
      [RECOMMENDATION_TYPES.Recommendation]: themeColors.lightYellow,
      [RECOMMENDATION_TYPES.Instruction]: themeColors.lightBlue,
    }),
    valueGetter: valueGetterAdvice,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    headerAlign: 'center',

    valueGetter: valueGetterName,
  },
  {
    field: 'selectedMandate',
    headerName: 'Mandate',
    flex: 2,
    headerAlign: 'center',
    valueGetter: (params, row) => {
      return row.selectedMandate;
    },
  },
  // {
  //   field: 'goal',
  //   headerName: 'Linked Goal',
  //   flex: 2,
  //   headerAlign: 'center',

  //   valueGetter: valueGetterGoal,
  // },
  {
    field: 'urgency',
    headerName: 'Urgency',
    flex: 1,
    headerAlign: 'center',

    valueGetter: valueGetterUrgency,
    renderCell: RenderColor({
      [URGENCY.NORMAL_ID]: themeColors.greyLight,
      [URGENCY.TAX_ID]: themeColors.lightGreen,
      [URGENCY.URGENT_ID]: themeColors.lightAlert,
      default: themeColors.greyLight,
    }),
  },
  {
    field: 'dateCreated',
    headerName: 'Date Created',
    flex: 1,
    headerAlign: 'center',
    renderCell: renderCellDateTime,
    valueGetter: (value, row, column, apiRef) => {
      return row?.dateCreated?.valueOf(); // used for ordering
    },
  },
  {
    field: 'dateUpdated',
    headerName: 'Last Updated',
    flex: 1,

    headerAlign: 'center',
    renderCell: renderCellDateTime,
    valueGetter: (value, row, column, apiRef) => {
      return row.dateUpdated?.valueOf(); // used for ordering
    },
  },
  {
    field: '__typename',
    headerName: ' ',
    flex: 1,
    headerAlign: 'center',

    renderCell: recommendGridRowActions,
  },
];

export const RecommendActions: React.FC<{recommendId: string}> = ({recommendId}) => {
  const {accountId, accountType} = useAccountContext();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {reviewId, reviewDetails, refreshReview} = useReviewContext();
  const isSppDA = accountType === BusinessUnitEnum.DA;
  const {refetch: sppRefetch} = useGetSppReview(accountId!, {skip: !isSppDA});
  const {data} = useReviewOVRecommends();
  const [getAssociatedReccs, {data: associatedReccsData, error: associatedReccsError}] =
    useLazyQuery<OvrecommendAssociatedReccs, OvrecommendAssociatedReccsVariables>(
      OV_RECOMMENDATION_ASSOCIATED_RECCS
    );
  const onCompletedDeleteRecommendation =
    onCompletedDeleteRecommendationFactory(enqueueSnackbar);
  const [deleteRecommendation] = useMutation<
    DeleteOvrecommend,
    DeleteOvrecommendVariables
  >(DELETE_OV_RECOMMENDATION, {
    onCompleted: onCompletedDeleteRecommendation,
    onError: (err) => {
      enqueueSnackbar(`Unable to delete the recommendation. ${err.message}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    },
    update: updateDeleteRecommendation,
    refetchQueries: ['ReviewDetails', 'GetSppReview'],
  });
  const recommends =
    data?.ovrecommends?.filter((r) => {
      if (!r?.review?.id) {
        return false;
      }
      const inThisReview = r.review.id === reviewId;
      return inThisReview;
    }) || [];

  const deleteSelectedRecommendation = deleteSelectedRecommendationFactory({
    recommends,
    enqueueSnackbar,
    getAssociatedReccs,
    closeSnackbar,
    accountId,
    reviewId,
    deleteRecommendation,
    refetch: isSppDA ? sppRefetch : refreshReview,
  });
  return (
    <Button
      data-test="Delete recc"
      variant="text"
      onClick={() => deleteSelectedRecommendation(recommendId)}
    >
      <Delete />
    </Button>
  );
};
